import React, { createContext, useContext, useMemo } from 'react';

type ChipSelectionScope = {
    /** UI container ID for chip selection/editing/popover state. An elementId for element chips; a Tiptap taskId for task-item chips. */
    containerId: string;
    /** The owning Milanote element ID whose selection gates chip activation. */
    elementId: string;
    /** Whether the owning element is the single canvas selection, gating select-mode chip activation. */
    isElementSingleSelected: boolean;
};

const ChipSelectionScopeContext = createContext<ChipSelectionScope | null>(null);

/**
 * Returns the chip selection scope provided by the nearest chip container.
 */
export const useChipSelectionScope = (): ChipSelectionScope => {
    const scope = useContext(ChipSelectionScopeContext);
    if (!scope) throw new Error('useChipSelectionScope must be used within a ChipSelectionScopeProvider');

    return scope;
};

type ChipSelectionScopeProviderProps = Pick<
    ChipSelectionScope,
    'containerId' | 'elementId' | 'isElementSingleSelected'
> & {
    children: React.ReactNode;
};

/**
 * Provides the host-level selection facts shared by every chip in a container.
 */
export const ChipSelectionScopeProvider: React.FC<ChipSelectionScopeProviderProps> = ({
    containerId,
    elementId,
    isElementSingleSelected,
    children,
}) => {
    const scope = useMemo(
        () => ({ containerId, elementId, isElementSingleSelected }),
        [containerId, elementId, isElementSingleSelected],
    );

    return <ChipSelectionScopeContext.Provider value={scope}>{children}</ChipSelectionScopeContext.Provider>;
};
